import React, { useState } from 'react';
import { BarChart2Icon, PieChartIcon, TrendingUpIcon, CalendarIcon, DownloadIcon, FilterIcon, RefreshCwIcon, UserIcon, TagIcon, DollarSignIcon } from 'lucide-react';
export function ExpenseReports({
  locale = 'en-US'
}) {
  const [dateRange, setDateRange] = useState('last3months');
  const [groupBy, setGroupBy] = useState('category');
  const [reportType, setReportType] = useState('summary');
  // Format currency based on locale
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Sample data for reports
  const reportData = {
    totalExpenses: 45750.25,
    expensesByCategory: [{
      category: 'Travel',
      amount: 18250.75,
      percentage: 39.9
    }, {
      category: 'Training',
      amount: 8750.5,
      percentage: 19.1
    }, {
      category: 'Meals',
      amount: 5980.25,
      percentage: 13.1
    }, {
      category: 'Marketing',
      amount: 4520.5,
      percentage: 9.9
    }, {
      category: 'Software',
      amount: 3950.0,
      percentage: 8.6
    }, {
      category: 'Office',
      amount: 2850.75,
      percentage: 6.2
    }, {
      category: 'Other',
      amount: 1447.5,
      percentage: 3.2
    }],
    expensesByDepartment: [{
      department: 'Sales',
      amount: 15250.5,
      percentage: 33.3
    }, {
      department: 'Marketing',
      amount: 12750.75,
      percentage: 27.9
    }, {
      department: 'Engineering',
      amount: 8950.0,
      percentage: 19.6
    }, {
      department: 'Finance',
      amount: 4520.5,
      percentage: 9.9
    }, {
      department: 'HR',
      amount: 2450.25,
      percentage: 5.4
    }, {
      department: 'Operations',
      amount: 1828.25,
      percentage: 4.0
    }],
    monthlyTrend: [{
      month: 'Jun',
      amount: 8250.5
    }, {
      month: 'Jul',
      amount: 9750.25
    }, {
      month: 'Aug',
      amount: 12500.75
    }, {
      month: 'Sep',
      amount: 7850.5
    }, {
      month: 'Oct',
      amount: 7398.25
    }],
    topExpenseUsers: [{
      name: 'John Doe',
      amount: 7850.75,
      count: 12
    }, {
      name: 'Sarah Williams',
      amount: 6950.5,
      count: 8
    }, {
      name: 'Robert Brown',
      amount: 5750.25,
      count: 10
    }, {
      name: 'Jane Smith',
      amount: 4850.5,
      count: 7
    }, {
      name: 'Michael Johnson',
      amount: 3750.25,
      count: 6
    }]
  };
  const getDataByGrouping = () => {
    switch (groupBy) {
      case 'department':
        return reportData.expensesByDepartment;
      case 'user':
        return reportData.topExpenseUsers.map(user => ({
          label: user.name,
          amount: user.amount,
          percentage: user.amount / reportData.totalExpenses * 100
        }));
      case 'category':
      default:
        return reportData.expensesByCategory;
    }
  };
  const getGroupLabel = () => {
    switch (groupBy) {
      case 'department':
        return 'Department';
      case 'user':
        return 'User';
      case 'category':
      default:
        return 'Category';
    }
  };
  return <div data-id="element-6427">
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-6" data-id="element-6428">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-6429">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6430">
            Expense Reports
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6431">
            Generate and view expense reports by different criteria
          </p>
        </div>
        {/* Report controls */}
        <div className="p-4 border-b border-gray-200 sm:p-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-6432">
          <div data-id="element-6433">
            <label htmlFor="date-range" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6434">
              Date Range
            </label>
            <select id="date-range" value={dateRange} onChange={e => setDateRange(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-6435">
              <option value="last3months" data-id="element-6436">Last 3 Months</option>
              <option value="last6months" data-id="element-6437">Last 6 Months</option>
              <option value="lastyear" data-id="element-6438">Last Year</option>
              <option value="ytd" data-id="element-6439">Year to Date</option>
              <option value="custom" data-id="element-6440">Custom Range</option>
            </select>
          </div>
          <div data-id="element-6441">
            <label htmlFor="group-by" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6442">
              Group By
            </label>
            <select id="group-by" value={groupBy} onChange={e => setGroupBy(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-6443">
              <option value="category" data-id="element-6444">Category</option>
              <option value="department" data-id="element-6445">Department</option>
              <option value="user" data-id="element-6446">User</option>
            </select>
          </div>
          <div data-id="element-6447">
            <label htmlFor="report-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6448">
              Report Type
            </label>
            <select id="report-type" value={reportType} onChange={e => setReportType(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-6449">
              <option value="summary" data-id="element-6450">Summary</option>
              <option value="detailed" data-id="element-6451">Detailed</option>
              <option value="trend" data-id="element-6452">Trend Analysis</option>
            </select>
          </div>
          <div className="flex items-end" data-id="element-6453">
            <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6454">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-6455" />
              Generate Report
            </button>
          </div>
        </div>
        {/* Report summary */}
        <div className="p-6" data-id="element-6456">
          <div className="mb-6" data-id="element-6457">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-6458">
              Expense Summary
            </h4>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-6459">
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-6460">
                <div className="p-5" data-id="element-6461">
                  <div className="flex items-center" data-id="element-6462">
                    <div className="flex-shrink-0" data-id="element-6463">
                      <DollarSignIcon className="h-6 w-6 text-indigo-600" data-id="element-6464" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-6465">
                      <dl data-id="element-6466">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6467">
                          Total Expenses
                        </dt>
                        <dd data-id="element-6468">
                          <div className="text-lg font-medium text-gray-900" data-id="element-6469">
                            {formatCurrency(reportData.totalExpenses)}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-6470">
                <div className="p-5" data-id="element-6471">
                  <div className="flex items-center" data-id="element-6472">
                    <div className="flex-shrink-0" data-id="element-6473">
                      <TagIcon className="h-6 w-6 text-green-600" data-id="element-6474" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-6475">
                      <dl data-id="element-6476">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6477">
                          Top {getGroupLabel()}
                        </dt>
                        <dd data-id="element-6478">
                          <div className="text-lg font-medium text-gray-900" data-id="element-6479">
                            {getDataByGrouping()[0].category || getDataByGrouping()[0].department || getDataByGrouping()[0].label}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-6480">
                <div className="p-5" data-id="element-6481">
                  <div className="flex items-center" data-id="element-6482">
                    <div className="flex-shrink-0" data-id="element-6483">
                      <UserIcon className="h-6 w-6 text-purple-600" data-id="element-6484" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-6485">
                      <dl data-id="element-6486">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6487">
                          Top Spender
                        </dt>
                        <dd data-id="element-6488">
                          <div className="text-lg font-medium text-gray-900" data-id="element-6489">
                            {reportData.topExpenseUsers[0].name}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Chart visualization */}
          <div className="mb-6" data-id="element-6490">
            <div className="flex justify-between items-center mb-4" data-id="element-6491">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-6492">
                Expenses by {getGroupLabel()}
              </h4>
              <button className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:text-gray-800 active:bg-gray-50" data-id="element-6493">
                <DownloadIcon className="h-4 w-4 mr-1" data-id="element-6494" />
                Export
              </button>
            </div>
            {/* Simple bar chart visualization */}
            <div className="h-80 relative" data-id="element-6495">
              <div className="absolute inset-0 flex items-end" data-id="element-6496">
                {getDataByGrouping().map((item, index) => <div key={index} className="flex flex-col items-center justify-end mx-2 flex-1" data-id="element-6497">
                    <div className="w-full bg-indigo-500 rounded-t" style={{
                  height: `${item.amount / reportData.totalExpenses * 100 * 3}px`,
                  maxHeight: '90%'
                }} data-id="element-6498"></div>
                    <div className="mt-2 text-xs text-gray-500 truncate w-full text-center" data-id="element-6499">
                      {item.category || item.department || item.label}
                    </div>
                    <div className="text-xs font-medium" data-id="element-6500">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Monthly trend */}
          <div className="mb-6" data-id="element-6501">
            <div className="flex justify-between items-center mb-4" data-id="element-6502">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-6503">
                Monthly Expense Trend
              </h4>
            </div>
            {/* Simple line chart visualization */}
            <div className="h-64 relative" data-id="element-6504">
              <div className="absolute inset-0 flex items-end" data-id="element-6505">
                {reportData.monthlyTrend.map((item, index) => <div key={index} className="flex flex-col items-center justify-end mx-2 flex-1" data-id="element-6506">
                    <div className="w-full bg-green-500 rounded-t" style={{
                  height: `${item.amount / 15000 * 100 * 2}px`,
                  maxHeight: '90%'
                }} data-id="element-6507"></div>
                    <div className="mt-2 text-xs text-gray-500" data-id="element-6508">
                      {item.month}
                    </div>
                    <div className="text-xs font-medium" data-id="element-6509">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Top expense users */}
          <div data-id="element-6510">
            <div className="flex justify-between items-center mb-4" data-id="element-6511">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-6512">
                Top Expense Users
              </h4>
            </div>
            <div className="overflow-x-auto" data-id="element-6513">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-6514">
                <thead className="bg-gray-50" data-id="element-6515">
                  <tr data-id="element-6516">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6517">
                      User
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6518">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6519">
                      # of Claims
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6520">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-6521">
                  {reportData.topExpenseUsers.map((user, index) => <tr key={index} data-id="element-6522">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-6523">
                        {user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-6524">
                        {formatCurrency(user.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-6525">
                        {user.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-6526">
                        {(user.amount / reportData.totalExpenses * 100).toFixed(1)}
                        %
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
}